"use client";

import { useEffect, useRef, useState } from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { LuRotateCw, LuX } from "react-icons/lu";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

function clampScale(scale: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

function pointerDistance(a: React.PointerEvent, b: React.PointerEvent) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function Lightbox({
  open,
  onOpenChange,
  src,
  alt,
  action,
  autoDismissMs,
  rotation: rotationProp,
  onRotationChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  src: string | null;
  alt: string;
  /** Extra control rendered next to the close button — e.g. a Retake
   * action for the post-capture glance overlay. */
  action?: React.ReactNode;
  /** Auto-closes after this many ms, unless the dialog closes/reopens
   * first. Used for the non-modal post-capture glance: a good shot
   * needs no tap, it just fades. */
  autoDismissMs?: number;
  /** When provided, rotation becomes controlled: this value is used
   * instead of internal state, and no longer resets on src/open change.
   * Pass this (with `onRotationChange`) when rotation must persist
   * beyond the lightbox's own lifetime, e.g. saved to a backend. */
  rotation?: number;
  /** Called with the next rotation value (0/90/180/270) when the user
   * clicks the rotate button, instead of the component managing it
   * internally. Only used when `rotation` is provided. */
  onRotationChange?: (rotation: number) => void;
}) {
  useEffect(() => {
    if (!open || !autoDismissMs) return;
    const t = setTimeout(() => onOpenChange(false), autoDismissMs);
    return () => clearTimeout(t);
    // `src` is intentional: a new capture while the overlay is still open
    // (scanner shoots again before the dismiss fires) restarts the window
    // for the new photo instead of cutting it short.
  }, [open, autoDismissMs, onOpenChange, src]);

  const isRotationControlled = rotationProp !== undefined;
  const [internalRotation, setInternalRotation] = useState(0);
  const rotation = isRotationControlled ? rotationProp : internalRotation;
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const pointersRef = useRef(new Map<number, React.PointerEvent>());

  // Reset transform whenever the shown image changes or the dialog closes.
  // Rotation is excluded from the reset when controlled — it's owned by
  // the caller and persists across images/opens.
  useEffect(() => {
    if (!isRotationControlled) setInternalRotation(0);
    setScale(MIN_SCALE);
    setOffset({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, open]);

  function rotate() {
    const next = (rotation + 90) % 360;
    if (isRotationControlled) onRotationChange?.(next);
    else setInternalRotation(next);
  }
  const rotateRef = useRef(rotate);
  rotateRef.current = rotate;

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "r" && e.key !== "R") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      rotateRef.current();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    e.stopPropagation();
    setScale(s => clampScale(s - e.deltaY * 0.01));
  }

  function onDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (scale > MIN_SCALE) {
      setScale(MIN_SCALE);
      setOffset({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    pointersRef.current.set(e.pointerId, e);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()] as [React.PointerEvent, React.PointerEvent];
      pinchRef.current = { distance: pointerDistance(a, b), scale };
      dragRef.current = null;
    } else if (scale > MIN_SCALE) {
      dragRef.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y };
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, e);
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()] as [React.PointerEvent, React.PointerEvent];
      const distance = pointerDistance(a, b);
      setScale(clampScale(pinchRef.current.scale * (distance / pinchRef.current.distance)));
      return;
    }
    if (dragRef.current) {
      setOffset({
        x: dragRef.current.offsetX + (e.clientX - dragRef.current.x),
        y: dragRef.current.offsetY + (e.clientY - dragRef.current.y),
      });
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) dragRef.current = null;
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal keepMounted>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-50 bg-black/90 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        <DialogPrimitive.Popup
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0"
          onClick={() => onOpenChange(false)}
        >
          <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>
          {src && (
            <img
              alt={alt}
              className="max-h-full max-w-full touch-none object-contain select-none"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
                transition: pointersRef.current.size > 0 || dragRef.current ? "none" : "transform 150ms ease-out",
                cursor: scale > MIN_SCALE ? "grab" : "default",
              }}
              onClick={e => e.stopPropagation()}
              onWheel={onWheel}
              onDoubleClick={onDoubleClick}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              src={src}
              draggable={false}
            />
          )}
          <div
            className="fixed top-[calc(env(safe-area-inset-top,0px)+1rem)] left-4 z-10"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Rotate image"
              onClick={rotate}
              className="flex size-11 items-center justify-center rounded-full bg-black/60 text-white active:scale-95"
            >
              <LuRotateCw className="size-5" />
            </button>
          </div>
          {action && (
            <div
              className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] left-1/2 z-10 -translate-x-1/2"
              onClick={e => e.stopPropagation()}
            >
              {action}
            </div>
          )}
          <DialogPrimitive.Close
            aria-label="Close preview"
            className="fixed top-[calc(env(safe-area-inset-top,0px)+1rem)] right-4 z-10 flex size-11 items-center justify-center rounded-full bg-black/60 text-white active:scale-95"
          >
            <LuX className="size-5" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { Lightbox };
